// @flow
import React, { Component, Children } from 'react';
import debounce from './utils/debounce';
import Frame from './Frame';
import Slide from './Slide';
import Pagination from './Pagination';

type CarouselProps = {
  resizeDebounce: number,
  duration: number,
  easing: string,
  perPage: number,
  startIndex: number,
  currentPage: number,
  draggable: boolean,
  threshold: number,
  loop: boolean,
  children: Node,
  onInit: () => void,
  onChange: ({ page: number }) => void,
  pagination: boolean,
  page: Node,
  container: Node,
  paginationContainer: Node,
  overflowVisible: boolean,
};

type CarouselState = {
  width: number,
  current: number,
};

class Carousel extends Component<CarouselProps, CarouselState> {
  static defaultProps = {
    resizeDebounce: 250,
    duration: 200,
    easing: 'ease-out',
    perPage: 1,
    currentPage: false,
    startIndex: 0,
    draggable: true,
    threshold: 20,
    loop: false,
    onInit: () => {},
    onChange: false,
    pagination: false,
    page: <button />,
    container: <div />,
    paginationContainer: (
      <div
        style={{
          textAlign: 'center',
        }}
      />
    ),
    overflowVisible: false,
  };

  state = {
    width: 0,
    current: 0,
  };

  componentDidMount() {
    if (!window) {
      return;
    }

    const { startIndex, resizeDebounce } = this.props;

    // eslint-disable-next-line react/no-did-mount-set-state
    this.setState({
      current: startIndex,
    });

    this.handleResize = debounce(() => {
      this.processResize();
    }, resizeDebounce);

    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    if (!window) {
      return;
    }

    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = null;
  selector = null;

  handleFrameChange = (data: { current: number }) => {
    this.changePage(data.current);
  };

  handlePageChange = (_e: any, page: number) => {
    this.changePage(page);
  };

  handleSelectorRef = (element: ?HTMLElement) => {
    this.selector = element;

    this.updateWidth();
  };

  processResize() {
    this.updateWidth();
  }

  updateWidth() {
    if (!this.selector) {
      return;
    }

    this.setState({
      width: this.selector.getBoundingClientRect().width,
    });
  }

  normalizeCurrent(slide: number, slidesCount: number) {
    const { perPage } = this.props;
    const firstPage = 0;
    const lastPage = slidesCount - perPage;

    if (slide <= firstPage) {
      return firstPage;
    }

    if (slide >= lastPage) {
      return lastPage;
    }

    return slide;
  }

  changePage(number: number) {
    const { currentPage, children, onChange } = this.props;
    const count = Children.count(children);

    if (currentPage === false) {
      this.setState({
        current: this.normalizeCurrent(number, count),
      });
    }

    if (typeof onChange === 'function') {
      onChange({ page: number, count });
    }
  }

  render() {
    const { children, pagination, paginationContainer, page, currentPage, ...rest } = this.props;
    const { perPage, overflowVisible } = this.props;
    const { width, current } = this.state;
    const slidesCount = Children.count(children);
    const slideWidth = 100 / slidesCount;

    const currentPageFinal = currentPage || current;
    const pageCount = slidesCount - perPage + 1;

    const styles = {
      overflow: overflowVisible ? 'visible' : 'hidden',
    };

    return (
      <div ref={this.handleSelectorRef} style={styles}>
        {width && (
          <Frame
            slidesCount={slidesCount}
            width={width}
            {...rest}
            parent={this.selector}
            current={currentPageFinal}
            draggable={slidesCount > perPage}
            onChange={this.handleFrameChange}
          >
            {Children.map(children, (child, index) => (
              <Slide key={index} child={child} width={slideWidth} />
            ))}
          </Frame>
        )}
        {pagination && (
          <Pagination
            paginationContainer={paginationContainer}
            page={page}
            current={currentPageFinal}
            count={pageCount}
            pageChange={this.handlePageChange}
          />
        )}
      </div>
    );
  }
}

export default Carousel;
